// #886 — read-only client access to `PAYROLL_PERIOD` and its `issues`
// subcollection (#884/#885/#886) for the month-close screen.
// `firestore.rules` allows any `employerMatches` reader (an employee may
// see their own month is locked) — only server-side callables ever write
// (Admin-SDK-only), so this file never writes, same convention as
// `payrollPreview.js`.

import { getFirestore, collection, doc, getDoc, query, where, limit, getDocs } from 'firebase/firestore'
import { useCollection } from 'react-firebase-hooks/firestore'
import { app } from './app'

const db = getFirestore(app)

// Duplicated from `functions/payrollPeriod/periodStatus.js` — same reasoning
// as `calendarMonthRange` above: a plain string, no logic, and `functions/`
// is a separate package this file cannot import from.
const PAYROLL_LOCKED_STATUS = 'PAYROLL_LOCKED'
const LEGACY_STATUS = 'LEGACY'
const OPEN_STATUS = 'OPEN'

/**
 * A calendar month as a `period_start`/`period_end` range, `YYYY-MM-DD`.
 * Mirrors `functions/payrollPeriod/payrollPeriodDoc.js`'s `calendarMonthRange`
 * — duplicated, not imported: that file lives in the `functions/` package,
 * and this is plain date arithmetic with no logic to drift.
 *
 * @param {number} month 1-12.
 * @param {number} year e.g. 2026.
 * @return {{periodStart: string, periodEnd: string}}
 */
export function calendarMonthRange(month, year) {
  const m = Number(month)
  const y = Number(year)
  const periodStart = `${y}-${String(m).padStart(2, '0')}-01`
  const lastDay = new Date(y, m, 0).getDate()
  const periodEnd = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
  return { periodStart, periodEnd }
}

/**
 * The `PAYROLL_PERIOD` for one employer+month, if one has ever been created
 * (created lazily, server-side, the first time the month is checked — a
 * month never checked has none yet, which is not an error).
 *
 * @param {object} params
 * @param {string|number} params.employerId
 * @param {number} params.year
 * @param {number} params.month
 * @returns {[import('firebase/firestore').QuerySnapshot|undefined, boolean, Error|undefined]}
 */
export function usePayrollPeriod({ employerId, year, month }) {
  const enabled = Boolean(employerId) && Boolean(year) && Boolean(month)
  const { periodStart } = enabled ? calendarMonthRange(month, year) : {}
  return useCollection(
    enabled
      ? query(
        collection(db, `employers/${employerId}/payrollPeriods`),
        where('period_start', '==', periodStart),
        limit(1),
      )
      : null,
  )
}

/**
 * #1138 — every OPEN `PAYROLL_PERIOD` for one employer, for a screen that
 * lets the user pick which open month a `RETROACTIVE_ADJUSTMENT` (spec §31
 * אפשרות ב') gets published into. Same direct-Firestore-read convention as
 * `usePayrollPeriod` above (`firestore.rules`: `employerMatches` read,
 * Admin-SDK-only write) — no callable needed for a read.
 *
 * @param {object} params
 * @param {string|number} params.employerId
 * @returns {[import('firebase/firestore').QuerySnapshot|undefined, boolean, Error|undefined]}
 */
export function useOpenPayrollPeriods({ employerId }) {
  const enabled = Boolean(employerId)
  return useCollection(
    enabled
      ? query(
        collection(db, `employers/${employerId}/payrollPeriods`),
        where('status', '==', OPEN_STATUS),
      )
      : null,
  )
}

/**
 * Every `MONTH_CLOSE_ISSUE` under one period — written whole by
 * `checkMonthClose` each time it runs (it replaces the set, never
 * accumulates), so this is always "as of the last check", not partial.
 *
 * @param {object} params
 * @param {string|number} params.employerId
 * @param {?string} params.periodId `null` while the period itself is still
 *   loading, or when none exists yet — the hook stays disabled.
 * @returns {[import('firebase/firestore').QuerySnapshot|undefined, boolean, Error|undefined]}
 */
export function usePeriodIssues({ employerId, periodId }) {
  const enabled = Boolean(employerId) && Boolean(periodId)
  return useCollection(
    enabled ? collection(db, `employers/${employerId}/payrollPeriods/${periodId}/issues`) : null,
  )
}

/**
 * Pure predicate over an already-fetched period doc (or `null`) — mirrors
 * `functions/payrollPeriod/findPayrollPeriod.js#isPeriodDocLocked` exactly
 * (#893: the Excel import needs the same "is this month locked" answer a
 * server-side write path already has, but from the browser, before the
 * write is even attempted).
 *
 * A period that does not exist yet, or that is explicitly `LEGACY`, is
 * NEVER locked. Everything else is locked exactly when `payroll_locked ===
 * true` AND `status === PAYROLL_LOCKED` — checking both, not deriving one
 * from the other, exactly like the server copy.
 *
 * @param {?object} period `{periodId, ...periodDocFields}` or `null`.
 * @return {boolean}
 */
export function isPeriodLocked(period) {
  if (!period || period.status === LEGACY_STATUS) {
    return false
  }
  return period.status === PAYROLL_LOCKED_STATUS && period.payroll_locked === true
}

/**
 * The `PAYROLL_PERIOD` for one employer+month, read once (not a live
 * listener) — for use inside an async write flow (e.g. the Excel import's
 * per-month-group loop), where a React hook cannot be called.
 *
 * @param {object} params
 * @param {string|number} params.employerId
 * @param {number} params.year
 * @param {number} params.month 1-12.
 * @return {Promise<?object>} `{periodId, ...periodDocFields}`, or `null`
 *   when no period document exists for this employer+month.
 */
export async function fetchPayrollPeriod({ employerId, year, month }) {
  const { periodStart } = calendarMonthRange(month, year)
  const snap = await getDocs(
    query(
      collection(db, `employers/${employerId}/payrollPeriods`),
      where('period_start', '==', periodStart),
      limit(1),
    ),
  )

  if (snap.empty) {
    return null
  }

  const found = snap.docs[0]
  return { periodId: found.id, ...found.data() }
}

/**
 * The `PAYROLL_PERIOD` by its own doc id, read once — for #1266's "reopen"
 * dialog on the retroactive-items screen, which only has `locked_period_id`
 * on the item (never a month/year) and needs the period's live
 * `export_status` to show the same already-exported warning
 * `ReopenMonthButton.js` shows, before the server enforces it.
 *
 * @param {object} params
 * @param {string|number} params.employerId
 * @param {string} params.periodId
 * @return {Promise<?object>} `{periodId, ...periodDocFields}`, or `null`
 *   when no such period document exists.
 */
export async function fetchPayrollPeriodById({ employerId, periodId }) {
  const snap = await getDoc(doc(db, `employers/${employerId}/payrollPeriods/${periodId}`))
  return snap.exists() ? { periodId: snap.id, ...snap.data() } : null
}
